import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pencil, Plus } from 'lucide-react'
import type { ConsoleUser, UserAccess } from './types'
import { fetchConsoleUsers, fetchUserAccess, upsertUserAccess } from './useAdminApi'
import { SettingsFormModal } from './SettingsFormModal'
import { AdminSearch, AdminTable, Banner, Btn, Field, FilterMenu, SectionHeader, Td, Th, inputCls } from './ui'
import { DEPARTMENTS, DEPARTMENT_LABEL, DepartmentPicker, SITES, SiteMultiSelect } from './accessPickers'

const EMPTY = { subject: '', department: 'operations' as string, sites: [] as string[] }

function accessMatches(row: UserAccess, query: string, department: string, site: string): boolean {
  if (department !== 'all' && row.department !== department) return false
  if (site !== 'all' && !row.sites.includes(site)) return false
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const hay = [
    row.subject,
    row.department,
    DEPARTMENT_LABEL[row.department] ?? '',
    ...row.sites,
  ]
  return hay.some((h) => h.toLowerCase().includes(needle))
}

/** Super-admin only -- the server enforces this too (403 on every /api/auth/access route
 *  for anyone else), same as UsersSection. QA: "only listed users from dropdown shall be
 *  allowed... no new user shall be created. It has to be from the list only." A subject
 *  is only ever an EXISTING console account now -- the Subject field is a search box over
 *  `users`, not free text, and save() itself re-validates the typed value resolves to one
 *  of them (not just the UI) since nothing stops a value being set some other way. The
 *  subject is also the row's primary key, so it's locked once a grant exists -- editing
 *  it here would upsert a second row rather than rename the first. */
export function AccessSection() {
  const [rows, setRows] = useState<UserAccess[]>([])
  const [users, setUsers] = useState<ConsoleUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSubject, setEditingSubject] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [subjectOpen, setSubjectOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [siteFilter, setSiteFilter] = useState('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // allSettled -- the users list is only used to populate the Subject dropdown's
      // suggestions, a nice-to-have. Its failure must not block this screen's primary
      // capability (seeing/editing access grants), unlike the fetchUserAccess failure.
      const [access, consoleUsers] = await Promise.allSettled([fetchUserAccess(), fetchConsoleUsers()])
      if (access.status === 'fulfilled') {
        setRows(access.value)
      } else {
        setError(access.reason instanceof Error ? access.reason.message : 'Failed to load access grants')
      }
      setUsers(consoleUsers.status === 'fulfilled' ? consoleUsers.value : [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const openNew = () => { setForm(EMPTY); setEditingSubject(null); setSubjectOpen(false); setModalOpen(true) }
  const openEdit = (row: UserAccess) => {
    setForm({ subject: row.subject, department: row.department, sites: row.sites })
    setEditingSubject(row.subject)
    setSubjectOpen(false)
    setModalOpen(true)
  }

  // Filtered on every keystroke, not just at open -- this box is a search box, not a
  // static list. Empty query shows every user (a browsable default), same as clicking
  // into it before typing anything.
  const subjectMatches = users.filter(
    (u) => u.username.toLowerCase().includes(form.subject.trim().toLowerCase()),
  )

  // management + zero sites is the one case where "no sites picked" means "every
  // site", not "no sites" -- serving/auth.py's _department_grants_admin() flips
  // is_admin=True for exactly this combination, bypassing check_site entirely. Every
  // other department's empty-sites case is the opposite (a lockout), which this screen
  // does not otherwise warn about (see SPEC.md Non-goals).
  const grantsFullAccessWithNoSites = form.department === 'management' && form.sites.length === 0

  const save = async () => {
    // Case-insensitive match against the EXISTING users list, not a free-text check --
    // typing something no account has, then saving anyway, must fail the same way
    // picking it from the dropdown never could. The UI (disabled-until-picked affordance
    // below) keeps most people from ever hitting this, but it is not the only guard.
    const matchedUser = users.find((u) => u.username.toLowerCase() === form.subject.trim().toLowerCase())
    if (!matchedUser) {
      setBanner({ tone: 'error', msg: 'Pick a user from the dropdown -- typing a new name is not allowed here.' })
      return
    }
    try {
      const saved = await upsertUserAccess(matchedUser.username, form.department, form.sites)
      setBanner({ tone: 'ok', msg: `Access for "${saved.subject}" saved` })
      setModalOpen(false)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Save failed' })
    }
  }

  const visible = useMemo(
    () => rows.filter((row) => accessMatches(row, query, departmentFilter, siteFilter)),
    [rows, query, departmentFilter, siteFilter],
  )
  const filteredEmpty = !loading && rows.length > 0 && visible.length === 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Department & site access"
        subtitle="Who sees which modules, scoped to which sites. IT-Support gets full access through the Users tab's admin role instead; Management gets it automatically when no site is picked -- picking specific sites scopes it down instead."
        action={<Btn onClick={openNew}><span className="inline-flex items-center gap-1"><Plus size={14} /> New grant</span></Btn>}
      />
      {banner && !(modalOpen && banner.tone === 'error') ? (
        <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.msg}</Banner>
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      <div className="flex flex-wrap items-center gap-2">
        <AdminSearch
          value={query}
          onChange={setQuery}
          label="Search access grants by username or role"
          placeholder="Search username or department"
        />
        <FilterMenu
          onClear={() => {
            setDepartmentFilter('all')
            setSiteFilter('all')
          }}
          groups={[
            {
              id: 'department', label: 'Department', value: departmentFilter, onChange: setDepartmentFilter,
              options: [{ id: 'all', label: 'All' }, ...DEPARTMENTS.map((d) => ({ id: d.id, label: d.label }))],
            },
            {
              id: 'site', label: 'Site', value: siteFilter, onChange: setSiteFilter,
              options: [{ id: 'all', label: 'All' }, ...SITES.map((s) => ({ id: s.id, label: s.label }))],
            },
          ]}
        />
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Subject</Th>
              <Th>Department</Th>
              <Th>Sites</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((row) => (
              <tr key={row.subject} className="hover:bg-slate-50/80">
                <Td mono>{row.subject}</Td>
                <Td>{DEPARTMENT_LABEL[row.department] ?? row.department}</Td>
                <Td>
                  {row.sites.length
                    ? row.sites.join(', ')
                    : row.department === 'management' ? 'All sites' : '—'}
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <button
                      type="button" onClick={() => openEdit(row)}
                      className="flex items-center gap-1 rounded p-1 text-xs text-slate-600 hover:bg-slate-100"
                      title="Edit grant"
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-400">No access grants yet</td></tr>
            ) : filteredEmpty ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-400">No users match this search</td></tr>
            ) : null}
          </tbody>
        </AdminTable>
      )}

      <SettingsFormModal
        open={modalOpen}
        title={editingSubject ? `Edit ${editingSubject}` : 'New grant'}
        subtitle="Scopes what this person's console account can see once they sign in."
        onClose={() => setModalOpen(false)}
        onSave={() => void save()}
        error={banner?.tone === 'error' ? banner.msg : null}
      >
        <div className="grid gap-3">
          <Field label="Subject" className="relative">
            <input
              className={inputCls} value={form.subject} autoFocus={!editingSubject}
              disabled={editingSubject !== null}
              placeholder="Search a user by name…"
              autoComplete="off"
              onFocus={() => setSubjectOpen(true)}
              // Delayed, not immediate -- an option button's own onClick needs to land
              // first. Both handlers run in the same tick a click causes, so the click's
              // setSubjectOpen(false) always wins; this timeout only fires (closing a now
              // stale-open list) when focus left for some OTHER reason, e.g. Tab/click
              // onto Department below.
              onBlur={() => { window.setTimeout(() => setSubjectOpen(false), 120) }}
              onChange={(e) => { setForm({ ...form, subject: e.target.value }); setSubjectOpen(true) }}
            />
            {editingSubject === null && subjectOpen ? (
              <ul
                role="listbox"
                className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
              >
                {subjectMatches.length ? subjectMatches.map((u) => (
                  <li key={u.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={form.subject === u.username}
                      onClick={() => { setForm({ ...form, subject: u.username }); setSubjectOpen(false) }}
                      className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    >
                      {u.username}
                    </button>
                  </li>
                )) : (
                  <li className="px-3 py-1.5 text-sm text-slate-400">No matching user</li>
                )}
              </ul>
            ) : null}
          </Field>
          <Field label="Department">
            <DepartmentPicker value={form.department} onChange={(id) => setForm({ ...form, department: id })} />
          </Field>
          <Field label="Sites">
            <SiteMultiSelect value={form.sites} onChange={(sites) => setForm({ ...form, sites })} />
          </Field>
          {grantsFullAccessWithNoSites ? (
            <p className="text-xs text-amber-600" role="status">
              No site selected -- this grants full access across every site.
            </p>
          ) : null}
        </div>
      </SettingsFormModal>
    </div>
  )
}
